// React
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import coreReducer from '../../../core/utils/reducer'

// Utils
import dayjs from 'dayjs'
import { findUserByID } from '../../Admin/system-users/utils/utils'
import { findAgiliteUser } from '../../Auth/utils/utils'
import { findPreviousLatestBooking, updateAllBookingsState } from '../../lib/booking-utils'
import { isAdmin, isDoctor, isNurse } from '../../lib/profile-utils'
import { generateUniqueUIDFromString, handleError } from '../../lib/utils'
import { executeBooking, getBookingState } from '../utils/bpm-utils'
import { generateActions, getMainTitle } from '../utils/lib'
import {
  generateAgoraRtcToken,
  getPlanSubOptions,
  goodXSubmit,
  readBillingRecords,
  readBookings,
  updateBooking
} from '../utils/utils'

// Components
import {
  faCalendarCheck,
  faCheckCircle,
  faExclamationTriangle,
  faFileAlt,
  faFilePrescription,
  faMoneyBill,
  faNotesMedical,
  faRoute,
  faStethoscope,
  faVideoCamera,
  faXmark
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, Col, Modal, Popconfirm, Select, Space, Tabs, message, notification, theme } from 'antd'
import CoreEnums from '../../../core/utils/enums'
import ServicesEnums from '../../Admin/services/utils/enums'
import AssessmentWrapper from '../../Examination/components/Assessment/AssessmentWrapper'
import ClinicalNotes from '../../Examination/components/Clinical Notes/ClinicalNotesTool'
import ScriptsWrapper from '../../Scripting/components/scripting-wrapper'
import { ContainerCard } from '../../reusable-components/AgiliteCards'
import AgiliteSkeleton from '../../reusable-components/AgiliteSkeleton'
import CustomRow from '../../reusable-components/CustomRow'
import EmployeeBookingsView from './Booking View Components/EmployeeBookingsListview/index'
import NurseToDoctor from './Examination Components/NurseToDoctor'
import PatientJourneyList from './Examination Components/PatientJourney'
import Billing from './Examination Components/billing'

// Styling
import GeneralChatbot from '../../AI Tools/General Chatbot'
import { AssistantEnums } from '../../AI Tools/assitants-enums'
import DocumentsWrapper from '../../Examination/components/Documents/DocumentsWrapper'
import { createInvoice, getInvoices } from '../../Invoices/utils'
import { settleCallpayOriginalTransaction } from '../../PeachPayments/utils/utils'
import CustomButton from '../../reusable-components/CustomButton'
import '../styles/index.css'
import MedProfLiveVideoWrapper from './Agora/MedProfLiveVideoWrapper'
import { TabContentWrapper } from './Examination Components/TabContentWrapper'
import { ProceduresProvider } from '../../Examination/examination-utils/ProceduresContext'
import { readBPMProfiles } from '../../Admin/clinics/utils/utils'
import { readClinicalNotes } from '../../Examination/examination-utils/examination-lib'

const PatientExaminationForm = ({ data, isPreviousBooking, workflowHistory, noAppointment, defaultTab }) => {
  const authState = useSelector((state) => state.auth)
  const state = useSelector((state) => state)

  const [activeTab, setActiveTab] = useState(
    defaultTab || (isNurse() || isDoctor() ? 'assessment' : isAdmin() ? 'journey' : 'billing')
  )
  const [agoraChannel, setAgoraChannel] = useState(null)
  const [agoraToken, setAgoraToken] = useState(null)
  const [agoraUid, setAgoraUid] = useState(null)
  const [billingItems, setBillingItems] = useState([])
  const [billingRecordsLoading, setBillingRecordsLoading] = useState(false)
  const [billingSyncRequired, setBillingSyncRequired] = useState(false)
  const [bookingData, setBookingData] = useState(data)
  const [completing, setCompleting] = useState(false)
  const [joined, setJoined] = useState(false)
  const [loading, setLoading] = useState(false)
  const [medProf, setMedProf] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [previousDataLoading, setPreviousDataLoading] = useState(false)
  const [previousLatestBooking, setPreviousLatestBooking] = useState(null)
  const [printingReport, setPrintingReport] = useState(false)
  const [saving, setSaving] = useState(false)
  const [controlWidth, setControlWidth] = useState(350)
  const [aiContext, setAiContext] = useState(null)
  const [statuses, setStatuses] = useState([])

  const { token } = theme.useToken()

  // Billing Web Socket
  const [billingWebSocket, setBillingWebSocket] = useState({
    connected: false,
    socket: null
  })

  const dispatch = useDispatch()

  const bookingSocketRef = useRef(null)

  useEffect(() => {
    if (!bookingSocketRef.current) {
      bookingSocketRef.current = new WebSocket(`${process.env.REACT_APP_NODE_RED_WS_URL}/billing`)
      bookingSocketRef.current.onopen = () => {
        setBillingWebSocket({
          connected: true,
          socket: bookingSocketRef.current
        })
      }
      bookingSocketRef.current.onmessage = (event) => {
        const data = JSON.parse(event.data)
        if (data.bookingRef === bookingData._id && data.userRef !== authState.agiliteUser._id) {
          setBillingSyncRequired(true)
        }
      }
      bookingSocketRef.current.onerror = (error) => {
        handleError(error, true)
      }
    }
    return () => {
      if (bookingSocketRef.current) {
        bookingSocketRef.current.close()
        setBillingWebSocket({
          connected: false,
          socket: null
        })
        bookingSocketRef.current = null
      }
    }
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    handleGetPatient()
    handleGetBillingRecords()
    // eslint-disable-next-line
  }, [])

  const handleGetStatuses = useCallback(async () => {
    let statuses = []

    try {
      if (data.clinicRecord.bpmConfig) {
        data.clinicRecord.bpmConfig.forEach((statusEntry) => {
          statuses.push({
            key: statusEntry.key,
            label: statusEntry.label,
            value: statusEntry.key
          })
        })
      }

      setStatuses(statuses)
    } catch (e) {
      message.error(handleError(e, true))
    }
  }, [data])

  const handleChangeStatus = async (statusValue, stepKey) => {
    let processRecord = null

    try {
      await updateBooking({ status: statusValue }, { _id: data._id })
      processRecord = await getBookingState([data.processRef])

      await executeBooking(
        'patient_journey',
        data.processRef,
        'submit',
        `${authState.agiliteUser.firstName} ${authState.agiliteUser.lastName}`,
        processRecord.key,
        stepKey
      )
      message.success('Status changed successfully.')
      // Update the local booking data to reflect the change
      setBookingData({ ...bookingData, status: statusValue })
    } catch (e) {
      message.error(handleError(e, true))
    }
  }

  useEffect(() => {
    handleGetStatuses()
    setBookingData(data)
  }, [data, handleGetStatuses])

  const handleGetPatient = async () => {
    let tmpPatient = null
    try {
      tmpPatient = await findAgiliteUser({ _id: bookingData.patientRecord._id })
      setBookingData({ ...bookingData, patientRecord: tmpPatient })
    } catch (e) {
      message.error(handleError(e, true))
    }
  }

  const handleGetBillingRecords = async () => {
    let tmpBillingRecords = []

    setBillingRecordsLoading(true)
    try {
      tmpBillingRecords = await readBillingRecords({ bookingRef: data._id })
      setBillingItems(tmpBillingRecords)
    } catch (e) {
      message.error(handleError(e, true))
    }
    setBillingRecordsLoading(false)
  }

  useEffect(() => {
    const getMedProf = async () => {
      let user = null

      try {
        user = await findUserByID(data.medicalProfRef)
        setMedProf(user)
      } catch (e) {
        message.error(handleError(e, true))
      }
    }

    getMedProf()

    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    handleGetPreviousLatestBooking()
    // eslint-disable-next-line
  }, [])
  const handleGetPreviousLatestBooking = async () => {
    let tmpData = null

    setPreviousDataLoading(true)

    try {
      tmpData = await findPreviousLatestBooking(data)
      setPreviousLatestBooking(tmpData)
    } catch (e) {
      message.error(handleError(e, true))
    }

    setPreviousDataLoading(false)
  }

  const closeTab = () => {
    dispatch(
      coreReducer.actions.closeTab({
        targetKey: state.core.tabNavigation.activeKey,
        removeBreadcrumb: true,
        newTabKey: noAppointment ? `${CoreEnums.tabKeys.PATIENTS}` : null
      })
    )
  }

  // Validate clinical notes before completion
  const validateClinicalNotes = async () => {
    try {
      const query = {
        bookingRef: bookingData._id,
        createdBy: authState.agiliteUser._id
      }
      const existingNotes = await readClinicalNotes(query)

      // Filter out empty notes and find notes with actual content
      const notesWithContent =
        existingNotes?.filter(
          (note) =>
            note.content &&
            note.content.trim() !== '' &&
            note.content !== '<p><br></p>' &&
            note.content.replace(/<[^>]*>/g, '').trim() !== '' // Remove HTML tags and check if there's actual text content
        ) || []

      return notesWithContent.length > 0
    } catch (error) {
      console.error('Error validating clinical notes:', error)
      return false
    }
  }

  const showClinicalNotesWarning = () => {
    Modal.confirm({
      title: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: token.colorWarning }} />
          <span>Clinical Notes Required</span>
        </div>
      ),
      content: (
        <div style={{ padding: '16px 0' }}>
          <p style={{ marginBottom: '16px' }}>
            Clinical notes are required before completing this booking. Please document the following information:
          </p>
          <ul style={{ paddingLeft: '20px', margin: '0' }}>
            <li>Chief complaint and history of present illness</li>
            <li>Physical examination findings</li>
            <li>Assessment and diagnosis</li>
            <li>Treatment plan and recommendations</li>
            <li>Any procedures performed</li>
          </ul>
          <p style={{ marginTop: '16px', fontWeight: 'bold', color: token.colorError }}>
            You cannot proceed without completing the clinical documentation.
          </p>
        </div>
      ),
      okText: 'Go to Clinical Notes',
      cancelText: 'Cancel',
      width: 500,
      onOk: () => {
        setActiveTab('clinicalNotes')
      },
      icon: null,
      okButtonProps: {
        type: 'primary',
        style: { backgroundColor: token.colorPrimary }
      },
      cancelButtonProps: {
        danger: true
      }
    })
  }

  const handleSave = async (isCompleteAction, recordData, isReport, generationFunction) => {
    let processRecord = null
    let newRecord = null

    // Validate clinical notes for doctors before completion
    if (isCompleteAction && isDoctor()) {
      const hasValidNotes = await validateClinicalNotes()
      if (!hasValidNotes) {
        showClinicalNotesWarning()
        return // Prevent completion
      }
    }

    if (isCompleteAction) {
      setCompleting(true)
    } else {
      setSaving(true)
    }

    setLoading(true)

    const nurseNotes = document.getElementById(`${data._id}-notes-capture-area-nurse`)?.value
    const doctorsNotes = document.getElementById(`${data._id}-notes-capture-area-doctor`)?.value
    const patientNotes = document.getElementById(`${data._id}-notes-capture-area-patient`)?.value

    try {
      // Notes Validation
      if (nurseNotes) {
        throw new Error('There are Nurse Clinical Notes that have not been saved. Please revise.')
      }

      if (doctorsNotes) {
        throw new Error('There are Clinical Notes that have not been saved. Please revise.')
      }

      if (patientNotes) {
        throw new Error('There are Patient Instruction Notes that have not been saved. Please revise.')
      }

      let latestRecordStatus = await readBookings({ _id: data._id })

      if (latestRecordStatus[0]?.status !== bookingData.status && !noAppointment) {
        throw new Error(`Unable to complete. ${data.status} may have been bypassed by another practitioner.`)
      }

      if (isCompleteAction) {
        processRecord = await getBookingState([data.processRef])

        const commonParams = [`${authState.agiliteUser.firstName} ${authState.agiliteUser.lastName}`, processRecord.key]

        switch (bookingData.status) {
          case CoreEnums.bookingStatuses.completed:
            // When the status is already completed, we know the doctor submitted updates
            processRecord = await executeBooking(
              'patient_journey',
              data.processRef,
              'completed',
              ...commonParams.concat('completed')
            )
            break
          case CoreEnums.bookingStatuses.screening:
            if (medProf.extraData.profession === 'nurse') {
              if (bookingData.isVirtualVisit) {
                // Submit to diagnosis
                processRecord = await executeBooking(
                  'patient_journey',
                  data.processRef,
                  'submit',
                  ...commonParams.concat('consultation')
                )
              } else if (bookingData.serviceRecord?.type === ServicesEnums.labels.VITALITY_HEALTH_CHECK) {
                // Submit to Reception Data Capture
                processRecord = await executeBooking(
                  'patient_journey',
                  data.processRef,
                  'submit',
                  ...commonParams.concat('reception')
                )
              } else {
                // Submit to Reception Billing
                processRecord = await executeBooking(
                  'patient_journey',
                  data.processRef,
                  'submit',
                  ...commonParams.concat('reception')
                )
              }
            } else {
              // Submit to diagnosis
              processRecord = await executeBooking(
                'patient_journey',
                data.processRef,
                'submit',
                ...commonParams.concat('consultation')
              )
            }

            break
          default:
            if (bookingData.nurseProcedureRequired && bookingData.status !== CoreEnums.bookingStatuses.screening) {
              processRecord = await executeBooking(
                'patient_journey',
                data.processRef,
                'submit',
                ...commonParams.concat('screening')
              )
            } else {
              // Check if Callpay settlement needs to be done
              if (medProf.extraData.profession === 'doctor' && bookingData.callpayGatewayReference) {
                await settleCallpayOriginalTransaction(bookingData.callpayGatewayReference)
              }

              processRecord = await executeBooking(
                'patient_journey',
                data.processRef,
                'submit',
                ...commonParams.concat('reception')
              )
            }
            break
        }
      }

      newRecord = await updateBooking(
        {
          status: isCompleteAction
            ? bookingData.clinicRecord.bpmConfig?.find((e) => e.key === processRecord.key)?.key
            : bookingData.status,
          noCharge: bookingData.noCharge
        },
        { _id: data._id }
      )

      updateAllBookingsState(newRecord)

      if (isReport) {
        setPrintingReport(true)
        await generationFunction(newRecord)
      }

      if (isCompleteAction) {
        message.success('Changes submitted successfully.')
        closeTab()
      } else {
        message.success('Changes saved successfully.')
      }
    } catch (e) {
      message.error(handleError(e, true))
    }
    setPrintingReport(false)
    setSaving(false)
    setCompleting(false)
    setLoading(false)
  }

  const startVirtualConsult = async () => {
    let agoraCreds = null
    let tmpUid = null

    try {
      tmpUid = generateUniqueUIDFromString(authState.agiliteUser._id)
      agoraCreds = await generateAgoraRtcToken(data._id, tmpUid)
      setAgoraToken(agoraCreds.token)
      setAgoraChannel(agoraCreds.channelName)
      setAgoraUid(tmpUid)
      setJoined(true)
    } catch (e) {
      message.error(handleError(e, true))
    }
  }

  const handleGenerateInvoice = async (goodXTicketNo, goodXSubmissionFailed = false) => {
    try {
      console.log('Creating invoice with billing items:', {
        billingItemsCount: billingItems?.length || 0,
        billingItems: billingItems,
        bookingRef: bookingData._id,
        goodXSubmissionFailed
      })

      // Determine invoice status based on GoodX submission result
      let invoiceType =
        bookingData.selectedInvoiceType || (bookingData.patientRecord?.medicalAid.name ? 'medical' : 'private')
      let goodXStatus = ''
      let accountGroup = 'medical_aid'

      if (goodXSubmissionFailed || !goodXTicketNo) {
        // If GoodX submission failed, mark as private
        invoiceType = 'private'
        goodXStatus = 'Private'
        accountGroup = 'private'
        console.log('Marking invoice as private due to GoodX submission failure')
      } else if (goodXTicketNo) {
        // Successful GoodX submission
        goodXStatus = 'Submitted'
        accountGroup = 'medical_aid'
      }

      const invoiceData = {
        userRef: bookingData.userRef,
        bookingRef: bookingData._id,
        medicalProfRef: bookingData.medicalProfRef,
        bookingDate: bookingData.bookingDate,
        clinicRef: bookingData.clinicRef,
        createdAt: dayjs(new Date()).format('YYYY-MM-DD HH:mm:ss'),
        createdBy: authState.agiliteUser._id,
        modifiedAt: dayjs(new Date()).format('YYYY-MM-DD HH:mm:ss'),
        modifiedBy: authState.agiliteUser._id,
        invoiceType,
        goodXStatus,
        accountGroup,
        invoiceNo: dayjs(new Date()).format('YYMMDDHHmmss'),
        goodXTicketNo,
        // Pass billing records directly to avoid timing issues
        billingRecords: billingItems
      }

      console.log('Creating invoice with billing integration:', {
        invoiceData,
        billingItemsForIntegration: billingItems
      })

      await createInvoice(invoiceData)

      // If GoodX submission failed and we're creating as private,
      // we need to adjust billing records to move medical aid amounts to patient amounts
      if (goodXSubmissionFailed && bookingData.selectedInvoiceType === 'medical') {
        console.log('Adjusting billing records for failed GoodX submission')

        try {
          // Get the created invoice ID to trigger transition
          const createdInvoices = await getInvoices({
            bookingRef: bookingData._id,
            invoiceNo: invoiceData.invoiceNo
          })

          if (createdInvoices.length > 0) {
            const { transitionInvoiceToPrivate } = await import(
              '../../Billing-Module-Enhanced/backend-integration/api/invoiceStatusTransition'
            )
            await transitionInvoiceToPrivate(createdInvoices[0]._id, 'goodx_submission_failed')
            console.log('Successfully adjusted billing records for private invoice')
          }
        } catch (transitionError) {
          console.error('Failed to transition billing records:', transitionError)
          // Don't fail the invoice creation, just log the error
        }
      }
    } catch (e) {
      message.error(handleError(e, true))
    }
  }

  const finalizeBilling = async () => {
    setLoading(true)
    setCompleting(true)

    const invoiceID = dayjs(new Date()).format('YYYYMMDDHHmmss').toString()
    let processRecord = await getBookingState([bookingData.processRef])
    // let inventoryItems = []
    let patientRequiredFields = ['dateOfBirth', 'idNo', 'firstName', 'lastName', 'gender', 'residentialAddress']
    let validationFailed = false
    let patient = null
    let mainMember = null
    let medicalProf = null
    let goodXTicketNo = ''
    // let medicalAidAmount = 0
    // let patientAmount = 0

    try {
      patient = await findAgiliteUser({ _id: bookingData.patientRecord._id })

      if (patient.mainMemberId) {
        mainMember = await findAgiliteUser({ _id: patient.mainMemberId })
      } else {
        mainMember = patient
      }

      medicalProf = await findAgiliteUser({ _id: bookingData.medicalProfRef })

      // TODO: THIS IS TEMPORARY
      // for (const item of billingItems) {
      //   medicalAidAmount += parseFloat(item.medicalAid)
      //   patientAmount += parseFloat(item.patient)
      // }

      // TODO: THIS IS TEMPORARY
      // await sendBill({ ...patient, ...data, medicalAidAmount, patientAmount })

      goodXTicketNo = await goodXSubmission(mainMember, patient, medicalProf)

      if (bookingData.clinicRecord.cgmEnabled && !bookingData.noCharge) {
        // Validation
        for (const entry of patientRequiredFields) {
          if (!patient[entry]) {
            validationFailed = true
          }
        }

        if (validationFailed) {
          setLoading(false)
          setCompleting(false)
          return notification.error({
            message: 'Patient Profile Incomplete',
            description: 'Please ensure that the patient profile is complete before proceeding with billing.',
            duration: 0
          })
        }
      }

      // BPM Billing Complete
      processRecord = await executeBooking(
        'patient_journey',
        bookingData.processRef,
        'submit',
        `${state.auth.agiliteUser.firstName} ${state.auth.agiliteUser.lastName}`,
        processRecord.key,
        'completed' // This is the nextStepKey used by BPM, here we are going from reception to booking complete
      )
      let newRecord = await updateBooking(
        {
          status: bookingData.clinicRecord.bpmConfig?.find((e) => e.key === processRecord.key)?.key,
          cgmInvoiceId: invoiceID,
          goodXTicketNo
        },
        { _id: bookingData._id }
      )
      updateAllBookingsState(newRecord)
      message.success('Billing Completed')
      closeTab()
    } catch (e) {
      if (typeof e === 'string') {
        message.error(handleError(e, true, true), 10)
      } else {
        message.error(handleError(e, true), 10)
      }
    }

    setLoading(false)
    setCompleting(false)
  }

  const goodXSubmission = async (mainMember, patient, medicalProf) => {
    let goodXTicketNo = ''
    let planSubOption = null
    let planSubOptionsData = null
    let goodXSubmissionFailed = false

    if (
      bookingData.selectedInvoiceType === 'medical' &&
      mainMember.medicalAid.name &&
      patient.medicalAid.name &&
      bookingData?.clinicRecord?.billingProfile?.switchingEnabled
    ) {
      try {
        planSubOptionsData = await getPlanSubOptions(
          mainMember.medicalAid.planOptionCode || patient.medicalAid.planOptionCode
        )

        if (planSubOptionsData.planSubOptions.pageResult.length > 0) {
          const tmpIndex = planSubOptionsData.planSubOptions.pageResult.findIndex(
            (option) => option.option === 'Acute Meds'
          )

          if (tmpIndex !== -1) {
            planSubOption = planSubOptionsData.planSubOptions.pageResult[tmpIndex]
          } else {
            planSubOption = planSubOptionsData.planSubOptions.pageResult[0]
          }
        }

        goodXTicketNo = await goodXSubmit({
          ...bookingData,
          patientRecord: patient,
          mainMemberRecord: mainMember,
          billingItems: billingItems.filter((item) => item.code && item.name && item.type),
          medicalProf,
          planSubOption
        })

        console.log('GoodX submission successful, ticket:', goodXTicketNo)
      } catch (error) {
        console.error('GoodX submission failed:', error)
        goodXSubmissionFailed = true

        // Show warning to user
        message.warning(
          'Medical aid submission failed. Invoice will be created as private. You can retry submission later.'
        )
      }
    }

    await handleGenerateInvoice(goodXTicketNo, goodXSubmissionFailed)

    return goodXTicketNo
  }

  const extraContent = {
    left: (
      <Button
        onClick={() => closeTab()}
        disabled={loading || saving || completing}
        style={{ marginLeft: 12, marginRight: 12 }}
      >
        <FontAwesomeIcon icon={faXmark} color={token.colorBorder} /> Close
      </Button>
    ),
    right: (
      <Space style={{ marginLeft: 12, marginRight: 12 }}>
        {bookingData.isVirtualVisit &&
        !joined &&
        authState?.agiliteUser?.extraData?.role.type === 'medical_professional' ? (
          <Popconfirm
            title='Confirmation'
            description='Are you sure you want to start the Virtual Consultation?'
            onConfirm={() => startVirtualConsult()}
            okText='Yes'
            cancelText='No'
            okButtonProps={{ type: 'primary', style: { backgroundColor: token.colorSuccess } }}
            cancelButtonProps={{ danger: true }}
            disabled={loading}
          >
            <Button disabled={loading} type='primary'>
              <FontAwesomeIcon icon={faVideoCamera} /> Start Virtual Consult
            </Button>
          </Popconfirm>
        ) : undefined}
        <Select
          style={{ width: 200 }}
          value={bookingData.status}
          placeholder='Select Status'
          options={statuses.map((status) => ({
            ...status,
            label: status.label
          }))}
          onChange={(value, option) => {
            handleChangeStatus(value, option.key)
          }}
        />
        {generateActions(bookingData.status) && !isPreviousBooking ? (
          <CustomButton
            size='small'
            loading={completing}
            onClick={() => {
              if (authState?.agiliteUser?.extraData?.role?.type === 'reception') {
                finalizeBilling()
              } else {
                if (bookingData.consultationRequired && authState.agiliteUser.extraData.profession === 'nurse') {
                  setModalOpen(true)
                } else {
                  handleSave(true)
                }
              }
            }}
            disabled={
              saving || authState?.agiliteUser?.extraData?.role?.type === 'reception' ? billingSyncRequired : false
            }
            tooltip={
              authState?.agiliteUser?.extraData?.role?.type === 'reception'
                ? billingSyncRequired
                  ? 'Please Refresh before you can Finalize Billing'
                  : undefined
                : undefined
            }
            type='success'
            style={{ backgroundColor: '#22C55E', borderColor: '#22C55E' }}
            hasPopconfirm={true}
            popConfirmTitle='Confirmation'
            popConfirmDescription={`Are you sure you want to ${
              data.status === CoreEnums.bookingStatuses.reception
                ? 'finalize billing'
                : data.status === CoreEnums.bookingStatuses.screening
                ? bookingData.consultationRequired && authState.agiliteUser.extraData.profession === 'nurse'
                  ? 'move on to the next step'
                  : data.medicalProfRef === authState.agiliteUser._id
                  ? 'complete the Examination without scheduling a virtual consult'
                  : 'complete the Examination'
                : data.status === CoreEnums.bookingStatuses.consultation
                ? 'complete the Consultation'
                : 'submit updates'
            }?`}
            text={
              data.status === CoreEnums.bookingStatuses.reception
                ? 'FINALIZE BILLING'
                : data.status === CoreEnums.bookingStatuses.screening
                ? bookingData.consultationRequired && authState.agiliteUser.extraData.profession === 'nurse'
                  ? 'SCHEDULE CONSULT'
                  : 'COMPLETE EXAMINATION'
                : data.status === CoreEnums.bookingStatuses.completed
                ? 'SUBMIT UPDATES'
                : 'Complete Consult'
            }
            icon={faCheckCircle}
            iconPosition='start'
          />
        ) : undefined}
      </Space>
    )
  }

  return (
    <ContainerCard
      id='scroll-detection'
      bodyStyle={{ paddingTop: 0 }}
      title={
        isPreviousBooking
          ? 'Previous Booking: ' + dayjs(data.bookingDate).format('DD-MM-YYYY')
          : getMainTitle(bookingData.status)
      }
    >
      <CustomRow justify='center' gutter={[0, 0]}>
        <Col span={12}>
          {joined ? (
            <MedProfLiveVideoWrapper
              channelName={agoraChannel}
              token={agoraToken}
              uid={agoraUid}
              disconnectCallback={() => {
                setJoined(false)
              }}
            />
          ) : undefined}
        </Col>
        <Col span={24}>
          <Tabs
            tabBarExtraContent={extraContent}
            activeKey={activeTab}
            onChange={(key) => {
              setActiveTab(key)
              if (key === 'billing') {
                handleGetBillingRecords()
              }
            }}
            tabBarStyle={{ background: 'white' }}
            className='no-margin'
          >
            {isNurse() || isDoctor() ? (
              <>
                <Tabs.TabPane
                  tab='Assessment'
                  key='assessment'
                  icon={<FontAwesomeIcon icon={faStethoscope} style={{ color: token.colorPrimary }} />}
                >
                  <CustomRow style={{ background: '#ffffff', paddingBottom: 24 }}>
                    <Col span={24}>
                      <TabContentWrapper
                        controlWidth={controlWidth}
                        setControlWidth={setControlWidth}
                        bookingData={bookingData}
                        setBookingData={setBookingData}
                        previousDataLoading={previousDataLoading}
                        previousLatestBooking={previousLatestBooking}
                        token={token}
                        tabTitle='Assessment'
                        tabIcon={<FontAwesomeIcon icon={faStethoscope} style={{ color: token.colorPrimary }} />}
                      >
                        <AssessmentWrapper
                          setAiContext={setAiContext}
                          bookingData={data}
                          setBookingData={setBookingData}
                        />
                      </TabContentWrapper>
                    </Col>
                  </CustomRow>
                </Tabs.TabPane>
                <Tabs.TabPane
                  tab='Notes'
                  key='clinicalNotes'
                  icon={<FontAwesomeIcon icon={faNotesMedical} style={{ color: token.colorPrimary }} />}
                >
                  <CustomRow style={{ background: '#ffffff', paddingBottom: 24 }}>
                    <Col span={24}>
                      <ProceduresProvider bookingData={bookingData} userRef={bookingData.userRef}>
                        <TabContentWrapper
                          controlWidth={controlWidth}
                          setControlWidth={setControlWidth}
                          bookingData={bookingData}
                          setBookingData={setBookingData}
                          previousDataLoading={previousDataLoading}
                          previousLatestBooking={previousLatestBooking}
                          token={token}
                          tabTitle='Clinical Notes & Diagnosis'
                          tabIcon={<FontAwesomeIcon icon={faNotesMedical} style={{ color: token.colorPrimary }} />}
                        >
                          <ClinicalNotes
                            bookingData={bookingData}
                            userRef={bookingData.userRef}
                            setModalOpen={setModalOpen}
                            noBooking={false}
                            setBookingData={setBookingData}
                            websocketConnection={billingWebSocket}
                          />
                        </TabContentWrapper>
                      </ProceduresProvider>
                    </Col>
                  </CustomRow>
                </Tabs.TabPane>
                {/* <Tabs.TabPane tab='Care Plans' key='carePlans'>
                  <CustomRow style={{ background: '#ffffff', paddingBottom: 24 }}>
                    <Col span={24}>
                      <PatientCarePlans userRef={bookingData?.userRef} />
                    </Col>
                  </CustomRow>
                </Tabs.TabPane> */}
                {/* <Tabs.TabPane tab='Protocol' key='protocol'>
                  <CustomRow style={{ background: '#ffffff', paddingBottom: 24 }}>
                    <Col span={24}>
                      <ProtocolAi bookingData={data} />
                    </Col>
                  </CustomRow>
                </Tabs.TabPane> */}
                <Tabs.TabPane
                  tab='Documents'
                  key='documents'
                  icon={<FontAwesomeIcon icon={faFileAlt} style={{ color: token.colorPrimary }} />}
                >
                  <CustomRow style={{ background: '#ffffff', paddingBottom: 24 }}>
                    <Col span={24}>
                      <TabContentWrapper
                        controlWidth={controlWidth}
                        setControlWidth={setControlWidth}
                        bookingData={bookingData}
                        setBookingData={setBookingData}
                        previousDataLoading={previousDataLoading}
                        previousLatestBooking={previousLatestBooking}
                        token={token}
                        tabTitle='Documents'
                        tabIcon={<FontAwesomeIcon icon={faFileAlt} style={{ color: token.colorPrimary }} />}
                      >
                        <DocumentsWrapper bookingData={data} />
                      </TabContentWrapper>
                    </Col>
                  </CustomRow>
                </Tabs.TabPane>
              </>
            ) : null}
            {isDoctor() ? (
              <Tabs.TabPane
                tab='Scripting'
                key='scripts'
                icon={<FontAwesomeIcon icon={faFilePrescription} style={{ color: token.colorPrimary }} />}
              >
                <CustomRow style={{ background: '#ffffff', paddingBottom: 24 }}>
                  <Col span={24}>
                    <TabContentWrapper
                      controlWidth={controlWidth}
                      setControlWidth={setControlWidth}
                      bookingData={bookingData}
                      setBookingData={setBookingData}
                      previousDataLoading={previousDataLoading}
                      previousLatestBooking={previousLatestBooking}
                      token={token}
                      tabTitle='Scripting'
                      tabIcon={<FontAwesomeIcon icon={faFilePrescription} style={{ color: token.colorPrimary }} />}
                    >
                      <ScriptsWrapper patientId={bookingData.userRef} bookingRef={bookingData._id} />
                    </TabContentWrapper>
                  </Col>
                </CustomRow>
              </Tabs.TabPane>
            ) : undefined}

            <Tabs.TabPane
              tab='Billing'
              key='billing'
              icon={<FontAwesomeIcon icon={faMoneyBill} style={{ color: token.colorPrimary }} />}
            >
              <CustomRow justify='center' style={{ background: '#ffffff', paddingBottom: 24 }}>
                <Col span={24}>
                  <TabContentWrapper
                    controlWidth={controlWidth}
                    setControlWidth={setControlWidth}
                    bookingData={bookingData}
                    setBookingData={setBookingData}
                    previousDataLoading={previousDataLoading}
                    previousLatestBooking={previousLatestBooking}
                    token={token}
                    tabTitle='Billing'
                    tabIcon={<FontAwesomeIcon icon={faMoneyBill} style={{ color: token.colorPrimary }} />}
                  >
                    <Billing
                      isReception={authState.agiliteUser.extraData.role.type === 'reception'}
                      billingItems={billingItems}
                      setBillingItems={setBillingItems}
                      bookingData={bookingData}
                      setBookingData={setBookingData}
                      handleGetBillingRecords={handleGetBillingRecords}
                      billingRecordsLoading={billingRecordsLoading}
                      setBillingSyncRequired={setBillingSyncRequired}
                      billingWebSocket={billingWebSocket}
                    />{' '}
                  </TabContentWrapper>
                </Col>
              </CustomRow>
            </Tabs.TabPane>
            {/* <Tabs.TabPane tab='Files' key='archive' icon={<FontAwesomeIcon icon={faFileArchive} />}>
              <CustomRow justify='center' style={{ background: '#ffffff', paddingBottom: 24 }}>
                <Col span={24}>
                  <TabContentWrapper
                    controlWidth={controlWidth}
                    setControlWidth={setControlWidth}
                    bookingData={bookingData}
                    setBookingData={setBookingData}
                    previousDataLoading={previousDataLoading}
                    previousLatestBooking={previousLatestBooking}
                    token={token}
                  >
                    <FileArchive bookingData={data} />{' '}
                  </TabContentWrapper>
                </Col>
              </CustomRow>
            </Tabs.TabPane> */}
            {(isDoctor() || isAdmin()) && !isPreviousBooking ? (
              <Tabs.TabPane
                tab='Bookings'
                key='previousBookings'
                icon={<FontAwesomeIcon icon={faCalendarCheck} style={{ color: token.colorPrimary }} />}
              >
                <CustomRow style={{ background: '#ffffff', paddingTop: 16 }}>
                  <Col span={24}>
                    <TabContentWrapper
                      controlWidth={controlWidth}
                      setControlWidth={setControlWidth}
                      bookingData={bookingData}
                      setBookingData={setBookingData}
                      previousDataLoading={previousDataLoading}
                      previousLatestBooking={previousLatestBooking}
                      token={token}
                      tabTitle='Bookings'
                      tabIcon={<FontAwesomeIcon icon={faCalendarCheck} style={{ color: token.colorPrimary }} />}
                    >
                      <EmployeeBookingsView userRef={bookingData.userRef} />{' '}
                    </TabContentWrapper>
                  </Col>
                </CustomRow>
              </Tabs.TabPane>
            ) : null}
            <Tabs.TabPane
              tab='Journey'
              key='patientJourney'
              icon={<FontAwesomeIcon icon={faRoute} style={{ color: token.colorPrimary }} />}
            >
              <CustomRow style={{ background: '#ffffff', paddingBottom: 24 }}>
                <Col span={24}>
                  <TabContentWrapper
                    controlWidth={controlWidth}
                    setControlWidth={setControlWidth}
                    bookingData={bookingData}
                    setBookingData={setBookingData}
                    previousDataLoading={previousDataLoading}
                    previousLatestBooking={previousLatestBooking}
                    token={token}
                    tabTitle='Patient Journey'
                    tabIcon={<FontAwesomeIcon icon={faRoute} style={{ color: token.colorPrimary }} />}
                  >
                    <PatientJourneyList workflowHistory={workflowHistory} />
                  </TabContentWrapper>
                </Col>
              </CustomRow>
            </Tabs.TabPane>
          </Tabs>
        </Col>
      </CustomRow>
      {modalOpen ? (
        <NurseToDoctor
          setData={setBookingData}
          data={bookingData}
          loading={loading}
          modalOpen={modalOpen}
          setModalOpen={setModalOpen}
          handleSave={handleSave}
        />
      ) : undefined}
      <Modal
        open={printingReport}
        maskClosable={false}
        closable={false}
        okButtonProps={{ style: { display: 'none' } }}
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        <CustomRow>
          <Col span={24}>
            <AgiliteSkeleton skActive spinnerTip='Printing report...' />
          </Col>
        </CustomRow>
      </Modal>

      {isDoctor() && activeTab === 'assessment' && (
        <GeneralChatbot
          assistantId={AssistantEnums.assistant_ids.MEDICAL_CHATBOT_ALT_2}
          JSONContext={JSON.stringify(aiContext)}
        />
      )}
    </ContainerCard>
  )
}

export default PatientExaminationForm
